import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, CheckCircle2, AlertCircle } from "lucide-react";
import { api } from "../lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-fc-navy text-white" data-testid="forgot-password-page">
      <div className="relative hidden lg:flex flex-col bg-[#060A24] p-12 overflow-hidden">
        <div className="absolute inset-0 fc-grid-pattern opacity-30 pointer-events-none" />
        <Link to="/" className="relative flex items-center gap-3 z-10">
          <img src="/brand/freddy-symbol.webp" alt="Freddy" className="h-10 w-auto" />
          <span className="text-white font-black tracking-[0.18em] text-xs">FREDDY COMPUTE ACADEMY</span>
        </Link>
        <div className="relative z-10 m-auto max-w-md w-full">
          <img src="/brand/freddy-seal.webp" alt="Freddy seal" className="w-28 h-28 object-contain mb-8 opacity-90" />
          <p className="fc-overline mb-4">PASSWORD RECOVERY</p>
          <h1 className="fc-h-section text-4xl xl:text-5xl leading-[1.05] mb-6">
            Reset your <span className="text-fc-red">access</span>.
          </h1>
          <p className="text-white/65 leading-relaxed text-sm">
            We'll send a one-time reset link to your inbox. Valid for 1 hour.
          </p>
        </div>
        <p className="relative z-10 text-xs text-white/35 tracking-wider">© 2026 Freddy Compute</p>
      </div>

      <div className="flex flex-col p-6 sm:p-12">
        <div className="m-auto w-full max-w-md">
          <h2 className="fc-h-section text-3xl sm:text-4xl mb-3">Forgot password?</h2>
          <p className="text-white/60 mb-8 text-sm">Enter the email tied to your Freddy account.</p>

          {sent ? (
            <div className="bg-[#10B981]/10 border border-[#10B981]/40 p-5 flex items-start gap-3" data-testid="forgot-success-msg">
              <CheckCircle2 className="text-[#10B981] flex-shrink-0 mt-1" size={18} />
              <div>
                <p className="font-bold mb-1">Check your email</p>
                <p className="text-white/70 text-sm leading-relaxed">
                  If an account exists with this email, we've sent a reset link.
                  <br />
                  <span className="text-white/50">(Dev mode: link is also printed to backend logs)</span>
                </p>
                <Link to="/login" className="inline-block mt-4 text-fc-red font-bold text-sm">← Back to login</Link>
              </div>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-5" data-testid="forgot-form">
              {error && (
                <div className="flex items-center gap-2 bg-fc-red/10 border border-fc-red/40 text-fc-red text-sm p-3">
                  <AlertCircle size={16} /> {error}
                </div>
              )}
              <label className="block">
                <span className="text-[10px] tracking-[0.22em] uppercase font-bold text-white/70 block mb-2">Email</span>
                <div className="relative">
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-[#060A24] border border-white/10 focus:border-fc-red text-white px-4 py-3 pl-10 outline-none transition-colors"
                    placeholder="you@dealership.com"
                    data-testid="forgot-email-input"
                  />
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" size={16} />
                </div>
              </label>
              <button type="submit" disabled={busy} className="btn-fc-red w-full justify-center" data-testid="forgot-submit-btn">
                {busy ? "Sending…" : "Send Reset Link"}
              </button>
              <p className="text-xs text-white/55 text-center">
                Remembered it? <Link to="/login" className="text-fc-red font-bold">Back to login</Link>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
